import React from "react";
import { useTranslation } from "react-i18next";
import { handleChange } from "../Utils/DataCollectionUtils";
import InputRowGroup from "./Inputs/InputRowGroup";
import InputRow from "./Inputs/InputRow";
import BooleanInput from "./Inputs/BooleanInput";
import TextInput from "./Inputs/TextInput";
import DropdownInput from "./Inputs/DropdownInput";
import NumberInput from "./Inputs/NumberInput";
import {
    PLAN_TYPE_IDS,
    calculateLifetimeDepositAmount,
    calculateYearlyDepositAmount,
} from "../Utils/PlanUtils";
import {
    calculateTotalInvestmentAssets,
    calculateTotalOtherAssets,
} from "../Utils/KYCUtils";
import "./DataCollection.css";

function arrayToDropdownOptions(array) {
    return array.map((item) => ({ label: item, value: item }));
}

const SuitabilityRationalDataCollection = ({ state }) => {
    const { t } = useTranslation();
    const kyc = state.knowYourClient;
    const setKyc = state.setKnowYourClient;
    const planTypeID = state.plan.planTypeID;

    let prefix = "";
    let planName = "";

    let alternativeNotSelectedPresets = [];
    let whyPutsInterestFirstOptions = [];

    if (planTypeID === PLAN_TYPE_IDS.GROUP) {
        prefix = "group";
        planName = "Group";

        alternativeNotSelectedPresets = [
            t(
                "The Subscriber(s) prefer a more structured, discipline-oriented savings approach.",
            ),
            t(
                "A flexible plan may not support the Subscriber(s)’ long-term savings behaviour.",
            ),
            t("The Subscriber(s) prefer predictable, scheduled contributions."),
            t(
                "The potential for group earnings aligns better with the Subscriber(s)’ long-term goal.",
            ),
            t(
                "The Subscriber(s) do not require or desire the level of flexibility offered by individual plans.",
            ),
            t(
                "The Subscriber(s)’ financial situation supports structured contribution commitments.",
            ),
            t(
                "The group structure aligns better with the Subscriber(s)’ education savings objectives.",
            ),
            t(
                "The Subscriber(s) prefer a plan that encourages consistent long-term contributions.",
            ),
            t(
                "The selected plan better supports the Subscriber(s)’ long-term behaviour and goals.",
            ),
            t(
                "The Subscriber(s) preferred the chosen plan after reviewing the trade-offs.",
            ),
        ];

        whyPutsInterestFirstOptions = [
            t(
                "Structured contributions support long-term savings discipline and align with the Subscriber(s)’ budget and cash-flow capacity, in accordance with CEFI’s Affordability Guidelines.",
            ),
            t(
                "The time horizon aligns with the GOP structure, and the Subscriber(s) can maintain the scheduled contribution requirements",
            ),
            t(
                "Compared to Achievers and SIOP, the GOP structure better supports the Subscriber(s)’ savings behaviour and goals.",
            ),
            t(
                "The Subscriber(s) acknowledged key trade-offs (contribution obligations and plan risks) and prefers the structured approach of the GOP.",
            ),
            t(
                "KYC was reviewed and confirmed. The recommendation reflects current personal/financial circumstances, objectives, risk tolerance and capacity, and time horizon.",
            ),
            t(
                "A reasonable range of alternatives was considered. The selected option best meets the Subscriber(s)’ needs.",
            ),
            t("See notes below"),
        ];
    } else if (planTypeID === PLAN_TYPE_IDS.ACHIEVERS) {
        prefix = "achievers";
        planName = "Achievers";

        alternativeNotSelectedPresets = [
            t(
                "The beneficiary’s age and selected plan align more closely with the Subscriber(s)’ time horizon.",
            ),
            t(
                "The Subscriber(s) prefer contribution flexibility rather than structured contributions.",
            ),
            t(
                "The Subscriber(s)’ income/cash flow is variable and cannot support scheduled contributions.",
            ),
            t(
                "The group plan structure is too restrictive for the Subscriber(s)’ circumstances.",
            ),
            t(
                "Contribution obligations may present an affordability risk for the Subscriber(s).",
            ),
            t(
                "The Subscriber(s) prefer full control over contribution timing and amounts.",
            ),
            t(
                "The Subscriber(s)’ risk capacity does not support mandatory contribution scheduling",
            ),
            t(
                "An individual plan option better matches the Subscriber(s)’ savings behaviour.",
            ),
            t(
                "The contribution requirements/structure did not match the Subscriber(s)’ cash flow.",
            ),
        ];

        whyPutsInterestFirstOptions = [
            t(
                "Goal-based Plan Contribution Targets provide structure with greater flexibility than a Group Option Plan, better matching the Subscriber(s)’ cash-flow variability.",
            ),
            t(
                "The Subscriber(s)’ risk tolerance and risk capacity align with Achievers’ features and the long-time horizon for education savings.",
            ),
            t(
                "Compared to GOP and SIOP, Achievers balances discipline and adaptability, meeting the Subscriber(s)’ needs and objectives.",
            ),
            t(
                "The Subscriber(s) understands plan risks, plan contribution targets, and fees, and this option puts the client’s interest first, relative to the alternatives.",
            ),
            t(
                "KYC was reviewed and confirmed. The recommendation reflects current personal/financial circumstances, objectives, risk tolerance and capacity, and time horizon.",
            ),
            t(
                "A reasonable range of alternatives was considered. The selected option best meets the Subscriber(s)’ needs.",
            ),
            t("See notes below"),
        ];
    } else if (planTypeID === PLAN_TYPE_IDS.SI) {
        prefix = "SI";
        planName = "Self-Initiated";

        alternativeNotSelectedPresets = [
            t(
                "The beneficiary’s age and selected plan align more closely with the Subscriber(s)’ time horizon.",
            ),
            t(
                "The Subscriber(s) prefer contribution flexibility rather than structured contributions.",
            ),
            t(
                "The Subscriber(s)’ income/cash flow is variable and cannot support scheduled contributions.",
            ),
            t(
                "The group plan structure is too restrictive for the Subscriber(s)’ circumstances.",
            ),
            t(
                "Contribution obligations may present an affordability risk for the Subscriber(s).",
            ),
            t(
                "The Subscriber(s) prefer full control over contribution timing and amounts.",
            ),
            t(
                "The Subscriber(s)’ risk capacity does not support mandatory contribution scheduling",
            ),
            t(
                "An individual plan option better matches the Subscriber(s)’ savings behaviour.",
            ),
            t(
                "The contribution requirements/structure did not match the Subscriber(s)’ cash flow.",
            ),
        ];

        whyPutsInterestFirstOptions = [
            t(
                "Maximum contribution flexibility best suits the Subscriber(s)’ variable income and liquidity needs without contractual contribution obligations.",
            ),
            t(
                "The Subscriber(s) wishes to open this plan to collect the Canada Learning Bond (CLB) without making contributions at this time.",
            ),
            t(
                "The time horizon and risk profile are compatible with SIOP features and the Subscriber(s)’ savings preferences.",
            ),
            t(
                "Compared to GOP and Achievers, SIOP provides the adaptability the Subscriber(s) needs to stay on track toward objectives.",
            ),
            t(
                "The Subscriber(s) understands the plan’s features, risks, and fees, and this option puts the client’s interest first given their circumstances.",
            ),
            t(
                "KYC was reviewed and confirmed. The recommendation reflects current personal/financial circumstances, objectives, risk tolerance and capacity, and time horizon.",
            ),
            t(
                "A reasonable range of alternatives was considered. The selected option best meets the Subscriber(s)’ needs.",
            ),
            t("See notes below"),
        ];
    } else {
        return null;
    }

    const isGroup = planTypeID === PLAN_TYPE_IDS.GROUP;
    const isAchievers = planTypeID === PLAN_TYPE_IDS.ACHIEVERS;
    const isSI = planTypeID === PLAN_TYPE_IDS.SI;

    return (
        <form>
            <InputRowGroup label={t(`${planName} Plan`)}>

                <InputRow
                    label={t(
                        "Does your recommendation consider the Subscriber(s)’ investment objectives and time horizon?",
                    )}
                >
                    <BooleanInput
                        name={`${prefix}RecommendationConsiderObjectivesAndTime`}
                        state={kyc}
                        onChange={handleChange(setKyc)}
                    />
                </InputRow>

                {isAchievers && (
                    <InputRow
                        label={t(
                            "Have you confirmed the reasonableness of the Subscriber(s)’ ability to meet the Plan Contribution Target?",
                        )}
                    >
                        <BooleanInput
                            name={`${prefix}ConfirmedReasonablenessOfContribution`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}

                {isGroup && (
                    <InputRow
                        label={t(
                            "Have you confirmed the Subscriber(s)’ ability to meet the plan’s structured contribution requirements?",
                        )}
                    >
                        <BooleanInput
                            name={`${prefix}AbilityToMeetRequirements`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}
                <InputRow
                    label={t(
                        "Have you reviewed and discussed all plan fees with the Subscriber(s)?",
                    )}
                >
                    <BooleanInput
                        name={`${prefix}ReviewedFees`}
                        state={kyc}
                        onChange={handleChange(setKyc)}
                    />
                </InputRow>
                {isGroup && (
                    <InputRow
                        label={t(
                            "Have you confirmed the Subscriber(s)’ ability to meet the plan’s structured contributions:",
                        )}
                    >
                        <BooleanInput
                            name={`${prefix}ConfirmedAbilityToMeetContributions`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}

                <InputRow
                    label={t(
                        "Does your recommendation consider the Subscriber(s)' risk tolerance and risk capacity?",
                    )}
                >
                    <BooleanInput
                        name={`${prefix}RecommendationConsidersRisk`}
                        state={kyc}
                        onChange={handleChange(setKyc)}
                    />
                </InputRow>

                {isSI && (
                    <InputRow
                        label={t(
                            "Do the Subscriber(s) have a need or preference for contribution flexibility?",
                        )}
                    >
                        <BooleanInput
                            name={`${prefix}NeedForFlexibility`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}

                {isAchievers && (
                    <InputRow
                        label={t(
                            "For this/these Subscriber(s), is there a need or preference for flexibility vs. structure?",
                        )}
                    >
                        <BooleanInput
                            name={`${prefix}NeedForFlexibility`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}

                <InputRow
                    label={t(
                        "Have you confirmed the Subscriber's willingness to make ongoing, structured contributions?",
                    )}
                >
                    <BooleanInput
                        name={`${prefix}ConfirmedWillingness`}
                        state={kyc}
                        onChange={handleChange(setKyc)}
                    />
                </InputRow>


                {(isAchievers || isSI) && (
                    <InputRow label={t("Considered Group")}>
                        <BooleanInput
                            name={`${prefix}ConsideredGroup`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}

                {(isGroup || isSI) && (
                    <InputRow label={t("Considered Achievers")}>
                        <BooleanInput
                            name={`${prefix}ConsideredAchievers`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}

                {(isGroup || isAchievers) && (
                    <InputRow label={t("Considered SI")}>
                        <BooleanInput
                            name={`${prefix}ConsideredSI`}
                            state={kyc}
                            onChange={handleChange(setKyc)}
                        />
                    </InputRow>
                )}




                <InputRow
                    label={t(
                        "If an alternative was not selected, briefly indicate why:",
                    )}
                    presetValues={alternativeNotSelectedPresets}
                    onPresetSelected={(preset) => {
                        setKyc({
                            ...kyc,
                            [`${prefix}AlternativeNotSelectedDescription`]:
                                preset,
                        });
                    }}
                >
                    <TextInput
                        name={`${prefix}AlternativeNotSelectedDescription`}
                        state={kyc}
                        onChange={handleChange(setKyc)}
                        fullWidth
                    />
                </InputRow>
                <InputRow
                    label={t(
                        "Why this plan puts the Subscriber’s interest first:",
                    )}
                >
                    <DropdownInput
                        name={`${prefix}WhyPutsInterestFirst`}
                        state={kyc}
                        onChange={handleChange(setKyc)}
                        useDefaultValue={false}
                        defaultPlaceholder={t('Select an option')}
                        options={arrayToDropdownOptions(
                            whyPutsInterestFirstOptions,
                        )}
                    />
                </InputRow>
                <InputRow label={t("Risk profile conclusion")}>
                    <DropdownInput
                        name="riskProfileConclusion"
                        state={kyc}
                        onChange={handleChange(setKyc)}
                        options={[
                            {
                                label: t("Select an option"),
                                value: "",
                            },
                            ...arrayToDropdownOptions([
                                t("Low"),
                                t("Medium"),
                                t("High"),
                            ])
                        ]}
                    />
                </InputRow>
                <InputRow
                    label={t(
                        "I believe the plan investment is suitable for the subscriber(s) because:",
                    )}
                >
                    <DropdownInput
                        name="whyPlanIsSuitable"
                        state={kyc}
                        onChange={handleChange(setKyc)}
                        useDefaultValue={false}
                        defaultPlaceholder={t('Select an option')}
                        options={arrayToDropdownOptions([
                            t(
                                "The subscriber(s) wish to save for post-secondary education and the proposed contributions are within CEFI’s affordability guidelines",
                            ),
                            t(
                                "The subscriber(s) wish to save for post-secondary education and the proposed plan is onside risk tolerance",
                            ),
                            t("Other - Please note in the KYC Notes Section"),
                        ])}
                    />
                </InputRow>
            </InputRowGroup>
        </form>
    );
};

export default SuitabilityRationalDataCollection;
